import { repository } from "~/repository";
import { useAuthStore } from "~/stores/auth/store";
import type { Collection, CollectionType } from "~/types";
import moment from "moment";
import { includes } from "lodash-es";
import { getAbstractEntityType } from "~/utils/common";

export default function (collection: {
  NotificationSet?: boolean;
  type: CollectionType;
  title: string;
  aired_at: string;
  id: string;
}) {
  const authStore = useAuthStore();
  const { user, authenticated } = storeToRefs(authStore);

  const toast = useToast();
  const confirm = useConfirm();

  const isFavorite = ref<boolean>(collection?.NotificationSet || false);
  const isNotificationsLoading = ref<boolean>(false);

  const notificationsTooltip = computed(() => {
    return isFavorite.value
      ? "Remove from notifications"
      : "Add to notifications";
  });

  const notificationsIcon = computed(() => {
    return isFavorite.value ? ["fa", "check"] : ["fas", "bell"];
  });

  async function toggleNotifications() {
    if (!collection) {
      toast.add({
        severity: "error",
        summary: "Action Failed",
        detail: "Collection data is unavailable.",
        life: 3000,
      });
      return;
    }

    if (!user.value.notificationsEnabled) {
      confirm.require({
        message: `Notifications are turned off. Setup notifications?`,
        header: "Notifications!",
        group: "message",
        rejectProps: {
          label: "Cancel",
        },
        acceptProps: {
          label: "Continue",
        },
        accept: async () => {
          await navigateTo("/notifications");
        },
      });
      return;
    }

    isNotificationsLoading.value = true;
    const payload = {
      content_type: getAbstractEntityType(collection.type),
      content_id: collection.id,
      enabled: !isFavorite.value,
    };

    if (isFavorite.value) {
      await repository.notifications.setNotificationContent(payload);
      isFavorite.value = false;

      toast.add({
        severity: "success",
        summary: "Removed from Notifications!",
        detail: `${collection.title} was successfully removed from your notifications !`,
        life: 3000,
      });
    } else {
      await repository.notifications.setNotificationContent(payload);

      isFavorite.value = true;

      toast.add({
        severity: "success",
        summary: "Added to Notifications!",
        detail: `${collection.title} was successfully added to your notifications !`,
        life: 3000,
      });
    }
    isNotificationsLoading.value = false;
  }

  const visible = computed(() => {
    if (!authenticated.value) {
      return false;
    } else if (includes(["collection", "playlist"], collection.type)) {
      return true;
    } else {
      const airDate = moment(collection.aired_at);
      const now = moment();
      return now.isBefore(airDate);
    }
  });

  return {
    notificationsVisible: visible,
    notificationsTooltip,
    notificationsIcon,
    toggleNotifications,
    isNotificationsLoading,
  };
}
